import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Scale, Plus, Activity, Server, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function LoadBalancers() {
  const { state, refresh } = useAzure();
  const rgs = state?.resource_groups || [];
  const pips = (state?.resources || []).filter((r) => r.type === "Microsoft.Network/publicIPAddresses");
  const vmsses = (state?.resources || []).filter((r) => r.type === "Microsoft.Compute/virtualMachineScaleSets");
  const [lbs, setLbs] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/azure/loadbalancer/config"); setLbs(data.load_balancers); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const after = async () => { await load(); refresh?.(); };

  return (
    <div data-testid="az-load-balancer">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-4">
        <Scale className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-semibold">Load Balancer — distribute traffic across healthy instances</h2>
      </div>
      <Tabs defaultValue="lbs">
        <TabsList>
          <TabsTrigger value="lbs" data-testid="az-lb-tab-lbs"><Scale className="h-4 w-4 mr-1.5" />Load balancers</TabsTrigger>
          <TabsTrigger value="config" data-testid="az-lb-tab-config"><ListChecks className="h-4 w-4 mr-1.5" />Configure</TabsTrigger>
        </TabsList>
        <TabsContent value="lbs" className="mt-4"><Create rgs={rgs} pips={pips} lbs={lbs} after={after} /></TabsContent>
        <TabsContent value="config" className="mt-4"><Configure lbs={lbs} vmsses={vmsses} after={after} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Create({ rgs, pips, lbs, after }) {
  const [name, setName] = useState(""); const [rg, setRg] = useState(""); const [pip, setPip] = useState(""); const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !rg) return toast.error("Enter a name and pick a resource group.");
    setBusy(true);
    try { await api.post("/azure/loadbalancer/create", { name, resource_group: rg, sku: "Standard", public_ip_id: pip || null }); toast.success(`Load balancer '${name}' created`); setName(""); await after(); }
    catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-border p-4 bg-card">
        <div className="text-sm font-medium mb-3">Create a load balancer</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Name</label><Input data-testid="az-lb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="lb-web-prod" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Resource group</label>
            <select data-testid="az-lb-rg" value={rg} onChange={(e) => setRg(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select…</option>{rgs.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}</select></div>
          <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Public IP (optional — Standard SKU)</label>
            <select data-testid="az-lb-pip" value={pip} onChange={(e) => setPip(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Internal (no public IP)</option>{pips.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        </div>
        <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={create} disabled={busy} data-testid="az-lb-create"><Plus className="h-4 w-4" />{busy ? "Creating…" : "Create load balancer"}</Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-lb-list">
        {lbs.length === 0 && <div className="text-sm text-muted-foreground">No load balancers yet.</div>}
        {lbs.map((lb) => (
          <div key={lb.id} className="border border-border rounded-md px-3 py-2.5" data-testid={`az-lb-${lb.name}`}>
            <div className="flex items-center gap-3"><Scale className="h-4 w-4 text-[#0078d4]" />
              <div className="text-sm font-medium">{lb.name}</div>
              <span className="text-[10px] bg-muted px-2 py-0.5 rounded">{lb.frontend?.type} · {lb.sku}</span></div>
            <div className="text-xs text-muted-foreground mt-1 ml-7">{lb.backend_pools.length} pool(s) · {lb.probes.length} probe(s) · {lb.rules.length} rule(s)</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Configure({ lbs, vmsses, after }) {
  const [lb, setLb] = useState("");
  useEffect(() => { if (!lb && lbs[0]) setLb(lbs[0].id); }, [lbs, lb]);
  const cur = lbs.find((x) => x.id === lb);
  const [pool, setPool] = useState(""); const [vmss, setVmss] = useState(""); const [probePort, setProbePort] = useState(80);
  const [rfront, setRfront] = useState(80); const [rback, setRback] = useState(80);
  useEffect(() => { if (!vmss && vmsses[0]) setVmss(vmsses[0].id); }, [vmsses, vmss]);
  const call = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); await after(); } catch (e) { toast.error(azErr(e)); } };

  if (lbs.length === 0) return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="az-lb-config-empty">Create a load balancer first.</div>;
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-3"><span className="text-xs text-muted-foreground">Load balancer</span>
        <select data-testid="az-lb-config-select" value={lb} onChange={(e) => setLb(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-sm">
          {lbs.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></div>

      <div className="rounded-lg border border-border p-4 bg-card space-y-4">
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><Server className="h-4 w-4" /> Backend pool</div>
          <div className="flex flex-wrap gap-2 items-end">
            <Input data-testid="az-lb-pool-name" value={pool} onChange={(e) => setPool(e.target.value)} placeholder="pool name" className="w-40" />
            <select data-testid="az-lb-pool-vmss" value={vmss} onChange={(e) => setVmss(e.target.value)} className="rounded-md border border-input bg-background px-2 py-2 text-sm">
              <option value="">No scale set</option>{vmsses.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
            <Button size="sm" variant="outline" data-testid="az-lb-pool-add" onClick={() => call("/azure/loadbalancer/backend-pool", { lb_id: lb, name: pool, vmss_id: vmss || null }, "Backend pool added")}>Add pool</Button>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{(cur?.backend_pools || []).map((p) => `${p.name} (${p.members?.length || 0})`).join(", ") || "none"}</div>
        </div>
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><Activity className="h-4 w-4" /> Health probe</div>
          <div className="flex flex-wrap gap-2 items-end">
            <Input data-testid="az-lb-probe-port" type="number" value={probePort} onChange={(e) => setProbePort(+e.target.value)} className="w-24" />
            <Button size="sm" variant="outline" data-testid="az-lb-probe-add" onClick={() => call("/azure/loadbalancer/probe", { lb_id: lb, name: `probe-${probePort}`, protocol: "Tcp", port: probePort }, "Health probe added")}>Add probe (TCP)</Button>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{(cur?.probes || []).map((p) => `${p.name} ${p.protocol}:${p.port}`).join(", ") || "none"}</div>
        </div>
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><ListChecks className="h-4 w-4" /> Load-balancing rule</div>
          <div className="flex flex-wrap gap-2 items-end">
            <div><span className="text-[10px] text-muted-foreground">front</span><Input data-testid="az-lb-rule-front" type="number" value={rfront} onChange={(e) => setRfront(+e.target.value)} className="w-20" /></div>
            <div><span className="text-[10px] text-muted-foreground">backend</span><Input data-testid="az-lb-rule-back" type="number" value={rback} onChange={(e) => setRback(+e.target.value)} className="w-20" /></div>
            <Button size="sm" className="bg-[#0078d4] text-white" data-testid="az-lb-rule-add" onClick={() => call("/azure/loadbalancer/rule", { lb_id: lb, name: `rule-${rfront}`, frontend_port: rfront, backend_port: rback, protocol: "Tcp" }, "Rule created")}>Add rule</Button>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{(cur?.rules || []).map((r) => `${r.name} ${r.frontend_port}→${r.backend_port}`).join(", ") || "none — needs a pool + probe first"}</div>
        </div>
      </div>
    </div>
  );
}
